import React, { useEffect, useState } from "react";
import { Flex, type TextProps } from "@chakra-ui/react";
import { animate, useMotionValue, useTransform } from "framer-motion";
import { ChakraBox, ChakraSpan } from "ui/chakra-framer";
import * as Sentry from "@sentry/nextjs";

const placeholderVariants = {
  visible: { opacity: 1 },
  fadeOut: (i: number) => ({
    opacity: [1, 0],
    transition: {
      delay: i - 0.3,
      duration: 0.3,
      ease: "easeInOut",
      repeat: 0,
    },
  }),
};

const cursorVariants = {
  hidden: { opacity: 0 },
  blinking: {
    opacity: [1, 1, 0, 0],
    transition: {
      duration: 1,
      repeat: Number.POSITIVE_INFINITY,
      repeatDelay: 0,
      ease: "linear",
      times: [0, 0.5, 0.5, 1],
    },
  },
};

export type TypewriterPlaceholderProps = {
  /**
   * Optional placeholder text to be displayed before typing starts
   */
  text: string;
  /**
   * The duration of the placeholder animation in seconds
   * @default 2
   */
  duration?: number;
};

export type TypewriterTextProps = {
  /**
   * The text to be typed out
   */
  text: string;
  /**
   * The duration of the typing animation in seconds
   * @default 3
   */
  duration?: number;
  /**
   * Optional callback function that is called when the typing animation begins
   */
  onStart?: () => void;
  /**
   * Optional callback function that is called when the typing animation is complete
   */
  onComplete?: () => void;
};

export type TypewriterProps = {
  /**
   * A boolean to determine if the typing animation is active
   *
   * Typically this is used to determine when the animation should start, ex. once it is in view
   */
  isAnimationPlaying?: boolean;
  /**
   * A boolean to determine if the typing animation should loop
   *
   * @default false
   */
  isLooped?: boolean;
  /**
   * Optional placeholder text to be displayed before typing starts
   */
  placeholder?: TypewriterPlaceholderProps;
  /**
   * An array of strings to be typed out in sequence
   */
  textSequence: TypewriterTextProps[];
  /**
   * The delay in milliseconds between each text typing animation
   *
   * @default 4000
   */
  transitionDelay?: number;
  /**
   * The font size of the text
   *
   * @default "md"
   */
  fontSize?: TextProps["fontSize"];
  /**
   * The line height of the text
   *
   * @default "20px"
   */
  lineHeight?: TextProps["lineHeight"];
};

export function Typewriter({
  isAnimationPlaying,
  isLooped,
  placeholder,
  textSequence,
  transitionDelay = 4000,
  fontSize = "md",
  lineHeight = "20px",
}: TypewriterProps) {
  const hasPlaceholder = Boolean(placeholder);
  const [placeholderVisible, setPlaceholderVisible] = useState(hasPlaceholder);
  const [isTyping, setIsTyping] = useState(!hasPlaceholder);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const currentSequence = textSequence[currentIndex];
  const displayText = useTransform(rounded, (latest) => currentSequence.text.slice(0, latest));

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    const completeAnimation = () => {
      setAnimationComplete(true);
    };

    const resetAnimation = () => {
      setCurrentIndex(0);
      count.set(0);
      setPlaceholderVisible(hasPlaceholder);
      setIsTyping(!hasPlaceholder);
    };

    const moveToNextText = () => {
      setCurrentIndex(currentIndex + 1);
      count.set(0);
    };

    const triggerAnimation = async () => {
      if (!isTyping || currentIndex >= textSequence.length) {
        return;
      }

      try {
        currentSequence.onStart?.();

        // Type out the current text in the sequence
        await animate(count, currentSequence.text.length, {
          duration: currentSequence.duration ?? 3,
          ease: "easeInOut",
        });

        // Determine the next action based on the current state
        if (!isLooped && currentIndex === textSequence.length - 1) {
          // Arbitrary delay of 2 seconds before setting the animation as complete and stopping the blinking cursor
          timeout = setTimeout(completeAnimation, 2000);
        } else if (isLooped && currentIndex === textSequence.length - 1) {
          timeout = setTimeout(resetAnimation, transitionDelay);
        } else {
          timeout = setTimeout(moveToNextText, transitionDelay);
        }

        currentSequence.onComplete?.();
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping, currentIndex]);

  useEffect(() => {
    if (!placeholderVisible && isAnimationPlaying) {
      setIsTyping(true);
    }
  }, [placeholderVisible, isAnimationPlaying]);

  return (
    <Flex align="center" boxSize="100%" fontWeight="medium" fontSize={fontSize} lineHeight={lineHeight} pos="relative">
      {placeholder && placeholderVisible && (
        <>
          <ChakraBox bg="text.primary" h={lineHeight} w="1px" variants={cursorVariants} animate="blinking" />
          <ChakraSpan
            color="product.gray.500"
            custom={placeholder.duration ?? 2}
            variants={placeholderVariants}
            animate={isAnimationPlaying && "fadeOut"}
            onAnimationComplete={() => {
              setPlaceholderVisible(false);
            }}
          >
            {placeholder.text}
          </ChakraSpan>
        </>
      )}

      {isTyping && (
        <>
          <ChakraSpan whiteSpace="nowrap">{displayText}</ChakraSpan>

          <ChakraBox
            bg="text.primary"
            h={lineHeight}
            w="1px"
            variants={cursorVariants}
            animate={animationComplete ? "hidden" : "blinking"}
          />
        </>
      )}
    </Flex>
  );
}
